/**
 * Authentication Context for React (Identity Platform)
 *
 * Provides authentication state and methods to all components.
 * Uses Firebase SDK with Google Cloud Identity Platform.
 * Supports: Email/Password (with registration & password reset), Google Sign-In
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  onAuthStateChange,
  signInWithGoogle,
  signInWithEmail,
  createAccount,
  resetPassword,
  signOut as firebaseSignOut,
  getIdToken,
  getAuthErrorMessage,
  User,
} from '../firebase';

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  provider: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  createAccount: (email: string, password: string, displayName?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearMessages: () => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Convert Firebase User to AuthUser
 */
function toAuthUser(firebaseUser: User): AuthUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    provider: firebaseUser.providerData[0]?.providerId || 'unknown',
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      if (firebaseUser) {
        setUser(toAuthUser(firebaseUser));
        setError(null);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const handleSignInWithGoogle = async () => {
    try {
      clearMessages();
      setLoading(true);
      await signInWithGoogle();
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    try {
      clearMessages();
      setLoading(true);
      await signInWithEmail(email, password);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (email: string, password: string, displayName?: string) => {
    try {
      clearMessages();
      setLoading(true);
      await createAccount(email, password, displayName);
      setSuccessMessage('Account created successfully!');
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      clearMessages();
      setLoading(true);
      await resetPassword(email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(getAuthErrorMessage(authError.code || ''));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      clearMessages();
      await firebaseSignOut();
      setUser(null);
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      setError(authError.message || 'Failed to sign out');
      throw err;
    }
  };

  /**
   * Get authorization headers for API requests
   */
  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const token = await getIdToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    successMessage,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    createAccount: handleCreateAccount,
    resetPassword: handleResetPassword,
    signOut: handleSignOut,
    clearMessages,
    getAuthHeaders,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
